import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GuideApi } from '../../api/guide-api';
import { HighGuideCompanySignals } from '../../models/product/dto/product-signal/high-guide-company-signals';
import { HighGuideProductSignal } from '../../models/product/dto/product-signal/high-guide-product-signal';
import { Ranking } from '../../models/product/dto/product-signal/ranking';
import { FileSystemService } from '../../services/file-system.service';
import { FetchOptions } from '../base/fetch-strategy';
import { Repository } from '../base/repository';

/**
 * Data-access layer for HighGuide product signal resources.
 *
 * Cache layout:
 *  - Product signals are user/company scoped, not location scoped, so they are keyed with a `null` locationId.
 *  - Rankings are location scoped and keyed with the real locationId, so each location keeps its own warm cache.
 *
 * The dashboard only reads these; every write goes through the embedded HighGuide iframe straight to the API, so
 * there is no write side here to keep the cache in step with. See `ProductSignalsDomainModel`.
 */
@Injectable()
export class ProductSignalsRepository extends Repository {
  constructor(
    fileSystemService: FileSystemService,
    protected guideAPI: GuideApi
  ) {
    super(fileSystemService);
  }

  private readonly productSignalsDataKey = 'highGuideProductSignals';
  private readonly productRankingsDataKey = 'highGuideProductRankings';

  /**
   * Company signals are scoped to the *requesting* user - the backend excludes that user's own signals from the
   * response - so the viewer has to be part of the cache key. `Repository` keys entries as
   * `${companyId}-${locationId}-${dataKey}` with no user slot, so the userId is folded into the dataKey.
   *
   * The locationId slot stays `null`: the response is company wide (signals come back with `locationId: 0`) even
   * though the websocket request carries a locationId for routing.
   */
  private companySignalsDataKey(userId: string): string {
    return `highGuideCompanySignals-${userId}`;
  }

  /**
   * The signed-in user's full signal list. Always fetched unfiltered - the id-filtered variant is a separate,
   * uncached method, because both would share this cache entry and a subset written here would stand in for the
   * whole list on the next cold load.
   */
  public getProductSignals$(companyId: number, opts?: FetchOptions): Observable<HighGuideProductSignal[] | null> {
    return this.loadArray$(
      HighGuideProductSignal,
      companyId,
      null,
      this.productSignalsDataKey,
      this.guideAPI.getProductSignals$(),
      opts
    );
  }

  /**
   * Uncached: returns only the named variants' signals. See `getProductSignals$`.
   */
  public getProductSignalsByVariantIds$(variantIds: string[]): Observable<HighGuideProductSignal[]> {
    return this.guideAPI.getProductSignals$(variantIds);
  }

  public getProductRankings$(companyId: number, locationId: number, opts?: FetchOptions): Observable<Ranking[] | null> {
    return this.loadArray$(
      Ranking,
      companyId,
      locationId,
      this.productRankingsDataKey,
      this.guideAPI.getProductRankings$(locationId),
      opts
    );
  }

  public getCompanySignals$(
    userId: string,
    companyId: number,
    locationId: number,
    opts?: FetchOptions
  ): Observable<HighGuideCompanySignals | null> {
    return this.loadInstance$(
      HighGuideCompanySignals,
      companyId,
      null,
      this.companySignalsDataKey(userId),
      this.guideAPI.getCompanySignals$(userId, companyId, locationId),
      opts
    );
  }
}
